'use client';

import React, { useState } from 'react';
import { X, Save, CheckCircle } from 'lucide-react';
import { saveBacktestRunAsync, generateRunId, buildDefaultResults, BacktestParams } from '@/app/backtest-history/utils/backtestStorage';

interface SaveBacktestModalProps {
  params: BacktestParams;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  onClose: () => void;
}

export default function SaveBacktestModal({ params, canvasRef, onClose }: SaveBacktestModalProps) {
  const [name, setName] = useState(`${params.symbol} ${params.timeframe} Backtest`);
  const [notes, setNotes] = useState('');
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    let chartSnapshot: string | undefined;
    try {
      if (canvasRef.current) {
        chartSnapshot = canvasRef.current.toDataURL('image/png');
      }
    } catch {
      // canvas may be tainted, skip snapshot
    }

    const run = {
      id: generateRunId(),
      name: name.trim() || `${params.symbol} ${params.timeframe} Backtest`,
      savedAt: new Date().toISOString(),
      params,
      results: buildDefaultResults(params),
      chartSnapshot,
      notes: notes.trim() || undefined,
    };

    await saveBacktestRunAsync(run);
    setSaved(true);
    setTimeout(() => onClose(), 1400);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Save size={15} className="text-primary" />
            <h2 className="text-sm font-bold text-foreground">Save Backtest Run</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
            <X size={15} />
          </button>
        </div>

        {saved ? (
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <CheckCircle size={36} className="text-positive" />
            <p className="text-sm font-600 text-foreground">Run saved successfully!</p>
            <p className="text-xs text-muted-foreground">Redirecting to history…</p>
          </div>
        ) : (
          <div className="px-5 py-5 space-y-4">
            {/* Run summary */}
            <div className="bg-muted/30 rounded-xl p-3.5 border border-border">
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div>
                  <p className="text-muted-foreground mb-0.5">Symbol</p>
                  <p className="font-600 text-foreground font-mono-nums">{params.symbol}</p>
                </div>
                <div>
                  <p className="text-muted-foreground mb-0.5">Timeframe</p>
                  <p className="font-600 text-foreground">{params.timeframe}</p>
                </div>
                <div>
                  <p className="text-muted-foreground mb-0.5">Capital</p>
                  <p className="font-600 text-foreground font-mono-nums">${parseInt(params.initialCapital).toLocaleString()}</p>
                </div>
                <div className="col-span-3">
                  <p className="text-muted-foreground mb-0.5">Period</p>
                  <p className="font-600 text-foreground font-mono-nums">{params.startDate} → {params.endDate}</p>
                </div>
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="block text-xs font-500 text-foreground mb-1.5">Run Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. NVDA Breakout Strategy v2"
                className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/60"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-xs font-500 text-foreground mb-1.5">Notes <span className="text-muted-foreground font-400">(optional)</span></label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="What did you observe? Any strategy adjustments to try next…"
                rows={3}
                className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/60 resize-none"
              />
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={handleSave}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary text-background text-sm font-600 hover:bg-primary/90 transition-colors"
              >
                <Save size={14} />
                Save to History
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2.5 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
